/**
 * `exactOptionalPropertyTypes` is on for the whole repo (02-nfr.md's strict set, root tsconfig),
 * and it means what it says: an optional prop may be ABSENT or it may hold a value, but it may not
 * be present and hold `undefined`. Most third-party component props are written the ordinary way,
 * so `<Input error={maybeUndefined} />` is a type error even though it does exactly what everyone
 * expects at run time.
 *
 * The choices are to loosen the flag for the web tree (no — it is a ruled setting and it catches
 * real bugs elsewhere), to write a ternary per prop (noisy, and easy to get one wrong), or to drop
 * the undefined keys before spreading. This is the third.
 */

type Defined<T> = { [K in keyof T]?: Exclude<T[K], undefined> };

/** Returns the same object with every `undefined`-valued key removed, so it can be spread. */
export function defined<T extends object>(props: T): Defined<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = value;
  }
  return out as Defined<T>;
}
